import { DrawCustomMode } from '@mapbox/mapbox-gl-draw';
import SimpleSelect from '@mapbox/mapbox-gl-draw/src/modes/simple_select';
import * as CommonSelectors from '@mapbox/mapbox-gl-draw/src/lib/common_selectors';
import * as Constants from '@mapbox/mapbox-gl-draw/src/constants';
import { point } from '@turf/turf';

/**
 * Custom select mode for Mapbox Draw.
 * Extends the built-in `SimpleSelect`.
 * See [documentation](https://github.com/mapbox/mapbox-gl-draw/blob/main/docs/MODES.md) for additional information on custom draw modes.
 */
export const SelectMode: DrawCustomMode = SimpleSelect;

/**
 * Triggered when the mouse is moved.
 * @param state a mutible state object created by `onSetup`
 * @param event the captured event that is triggering this lifecycle event
 */
SelectMode.onMouseMove = function(state, event): void {
    // Check if mouse hovers over a story
    let map = event.target;
    const storyFeatures = map.queryRenderedFeatures(event.point, { layers: ['storyClickLayer'] });

    // Query all features at the cursor position
    // ! During testing, this query has not resulted in performance issue. If issues arise later on, consider throttling the query
    // TODO: Fix types
    // ? Change event type in `featuresAt´ from `Event` to `MapboxEvent<MouseEvent | TouchEvent>`
    // ? Make method accept either `event` or `bbox`parameter (only one is used internally)
    // @ts-ignore
    const features = this.featuresAt(event);
    // Check if the topmost feature is a route segment
    const topmostFeature = features[0];
    // TODO: Should use isFeatureRouteSegment() type guard but featuresAt() is mistakingly annotated as returning `DrawFeature[]` when in reality it returns `Feature[]` (type exported from mapbox-gl-js/src/util/vectortile_to_geojson.js)
    const routeSegment = topmostFeature?.properties?.user_meta == 'route-segment' && storyFeatures.length <= 0 ? topmostFeature : null;

    // Update state
    state.isHovering = Boolean(routeSegment);
    state.routeSegmentId = routeSegment?.properties.id;
    state.storyKey = routeSegment?.properties.user_storyKey;
    state.position = event.lngLat;

    // If the user is hovering over a line, force a render
    if(state.isHovering) {
        // Add a hidden feature which can be passed to `this.doRender()` to prevent redrawing existing features
        if(!state.hiddenFeature) {
            state.hiddenFeature = this.newFeature(point([0, 0], { meta: 'hidden' }));
            this.addFeature(state.hiddenFeature);
        }
        this.doRender(state.hiddenFeature.id);
    }
};

/**
 * Triggered when the mouse button is pressed down.
 * @param state a mutible state object created by `onSetup`
 * @param event the captured event that is triggering this lifecycle event
 */
SelectMode.onMouseDown = function(state, event): void {
    if(state.isHovering) {
        // If the user is trying to add a waypoint, force a clean render
        state.isHovering = false;
        this.doRender(state?.hiddenFeature?.id);
        // Make sure hover was on a route (timing issue)
        if(!state?.routeSegmentId) return;
        // and switch to waypoint mode
        const modeOptions = { position: state.position, routeSegmentId: state.routeSegmentId, storyKey: state.storyKey };
        // @ts-ignore
        // TODO: Figure out how to extend the `DrawModes` interface
        this.changeMode('waypoint', modeOptions);
    } else {
        // Otherwise, use default behavior from `SimpleSelect` (where box selection has been intentionally left out)
        // ! Some methods on `SimpleSelect` has to be invoked using `call(this, ...)` to pass along the correct context
        // `MapboxDraw` distributes this context to the custom draw modes during initialization, but it is not passed further down to `SimpleSelect` when importing it in this module
        if(CommonSelectors.isActiveFeature(event)) return SimpleSelect.startOnActiveFeature.call(this, state, event);
    }
};

/**
 * `toDisplayFeatures` is the only required function for a mode.
 * It decides which features currently in Draw's data store will be rendered on the map.
 * All features passed to `display` will be rendered, so you can pass multiple display features per internal feature.
 * See the *Styling Draw* section in the API documentation for advice on making display features: https://github.com/mapbox/mapbox-gl-draw/blob/main/docs/API.md#styling-draw
 * @param state a mutible state object created by `onSetup`
 * @param geojson a GeoJSON being evaulated. To render, pass to display
 * @param display all GeoJSON objects passed to this will be rendered onto the map
 */
SelectMode.toDisplayFeatures = function(state, geojson, display) {
    // If the user is hovering, render a temporary waypoint
    if(state.isHovering) {
        const waypoint = point([state.position.lng, state.position.lat], { meta: 'waypoint' });
        display(waypoint);
    }

    // Below follows the default behavior from `SimpleSelect`
    // @ts-ignore
    geojson.properties.active = (this.isSelected(geojson.properties.id)) ? Constants.activeStates.ACTIVE : Constants.activeStates.INACTIVE;
    display(geojson);
    // ! Some methods on `SimpleSelect` has to be invoked using `call(this, ...)` to pass along the correct context
    SimpleSelect.fireActionable.call(this);
};
